/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/core';
import styled from '@emotion/styled';
import React, { useEffect, useRef, useState } from 'react';
import { Modal, CenteredLoadingIndicator } from '@spotify-internal/anchor-ds';
import { ButtonPrimary } from '@spotify-internal/encore-web';
import { useT } from 'i18n/hooks/useT';
import { useBrowserSize } from '../../../contexts/BrowserSize';
import { ButtonWithHoverAndPress } from '../../../shared/Button';
import { msToDigital } from '../../../utils';
import { Caller } from '../Caller';
import { ConnectionAlert } from '../ConnectionAlert';
import { Timer } from '../Timer';
import styles from './styles.sass';

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  padding: 20px;
`;

const LoadingContainer = styled.div`
  margin-bottom: 32px;
`;

const Rule = styled.hr`
  width: 100%;
  max-width: 540px;
  height: 2px;
  background-color: #d8d8d8;
  @media (max-width: 600px) {
    display: none;
  }
`;

const recordBlink = keyframes`
  0% {
    opacity: 0
  }
  50% {
    opacity: 1
  }
  100% {
    opacity: 0
  }
`;

const RecordDot = styled.div`
  width: 9px;
  height: 9px;
  margin-right: 8px;
  border-radius: 100%;
  background-color: #fd6767;
  animation: ${recordBlink} 2s ease infinite;
`;

const baseCss = `
    position: absolute;
    left: 0;
    width: calc(100% - 4px);
    height: 10px;
`;
interface CurrentlyOnCallProps {
  participants: any[];
  onPressLeave: any;
  session: any;
  publisher: any;
  streams: any | null;
}

// this is used to calculate the dynamic height of the participant list
// this value is the sum of the height of:
// header, paddings, timer, button, and/or connection alert
const HEIGHT_OFFSET_WITH_ALERT = 395;
const HEIGHT_OFFSET = 323;

const CurrentlyOnCall = ({
  participants,
  onPressLeave,
  session,
  publisher,
  streams,
}: CurrentlyOnCallProps) => {
  const t = useT();
  const [isOnline, setIsOnline] = useState(true);
  const [showNotConnectedAlert, setShowNotConnectedAlert] = useState(false);
  const [
    isShowingLeaveConfirmationModal,
    setIsShowingLeaveConfirmationModal,
  ] = useState(false);
  const timeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);
  const [notSubscribedUuids, setNotSubscribedUuids] = useState<string[]>([]);
  const notSubscribedUuidsRef = useRef<string[]>([]);
  const { height } = useBrowserSize();
  const participantListHeight =
    height - (showNotConnectedAlert ? HEIGHT_OFFSET_WITH_ALERT : HEIGHT_OFFSET);

  const displayParticipants = getParticipants();

  useEffect(() => {
    function clearAlertTimeout() {
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
    }
    clearAlertTimeout();
    if (notSubscribedUuids.length > 0) {
      timeoutRef.current = setTimeout(() => {
        setShowNotConnectedAlert(true);
      }, 5000);
    } else {
      setShowNotConnectedAlert(false);
    }
    return () => {
      clearAlertTimeout();
    };
  }, [notSubscribedUuids.length]);

  useEffect(() => {
    const audioElement = document && document.querySelector('audio');
    if (audioElement) {
      audioElement.volume = 0;
      audioElement.muted = true;
    }
    window.onunload = () => {
      if (session && publisher) {
        session.unpublish(publisher);
      }
    };
    function setOnline() {
      setIsOnline(true);
    }
    function setOffline() {
      setIsOnline(false);
    }
    window.addEventListener('online', setOnline);
    window.addEventListener('offline', setOffline);
    window.onbeforeunload = () => {
      return true;
    };
    return function cleanup() {
      window.onbeforeunload = null;
      window.removeEventListener('online', setOnline);
      window.removeEventListener('offline', setOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getParticipants() {
    if (streams === null) return [];
    return (
      participants
        .filter(p => {
          // only show users that have a opentok stream
          return p.isSelf || streams[p.uuid] !== undefined;
        })
        // always have the host be the first person in the list of people
        .sort((a, b) => {
          if (a.isHost) return -1;
          if (b.isHost) return 1;
          return 0;
        })
    );
  }

  return (
    <Container>
      <div>
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
          `}
        >
          <RecordDot />
          <h2
            css={css`
              margin: 0;
              color: #292f36;
              font-weight: bold;
              font-size: 2rem;
            `}
          >
            {t(
              'CallJoin.CurrentlyOnCall.Header',
              'Recording',
              'Call with friends feature already in a call header'
            )}
          </h2>
        </div>
        <Timer interval={1000}>
          {({ elapsedTimeInMs }: any) => (
            <h2
              css={css`
                margin: 20px 0;
                color: #292f36;
                font-weight: 800;
                font-size: 5rem;
                text-align: center;
              `}
            >
              {`${msToDigital(elapsedTimeInMs)}`}
            </h2>
          )}
        </Timer>
        <Rule />
        <div
          css={css`
            position: relative;
            width: 100%;
            max-width: 400px;
            height: 100%;
            max-height: 354px;
            margin: auto;
            @media (max-width: 600px) {
              max-height: ${participantListHeight}px;
            }
          `}
        >
          <div
            css={css`
              ${baseCss}
              top: 0;
              background: linear-gradient(
                to bottom,
                #ffffff,
                rgba(255, 255, 255, 0)
              );
            `}
          />
          <div
            css={css`
              ${baseCss}
              bottom: 0;
              background: linear-gradient(
                to top,
                #ffffff,
                rgba(255, 255, 255, 0)
              );
            `}
          />
          <div
            className={styles.participantList}
            css={css`
              @media (max-width: 600px) {
                max-height: ${participantListHeight}px;
              }
            `}
          >
            {displayParticipants.map(participant => {
              const {
                userId,
                deviceKind,
                displayName,
                isHost,
                isSelf,
                uuid,
              } = participant;
              const stream = streams[uuid];
              return (
                <Caller
                  key={`caller-item-${uuid}`}
                  stream={stream}
                  session={session}
                  userId={userId}
                  deviceKind={deviceKind}
                  displayName={displayName}
                  isHost={isHost}
                  isSelf={isSelf}
                  uuid={uuid}
                  handleSubscribe={(isSubscribed: boolean) => {
                    // this helps determine whether or not we need to show the connection error alert
                    let newUuids: string[] = [];
                    if (isSubscribed) {
                      newUuids = notSubscribedUuidsRef.current.filter(
                        notSubscribedUuid => notSubscribedUuid !== uuid
                      );
                      setNotSubscribedUuids(newUuids);
                      notSubscribedUuidsRef.current = newUuids;
                    } else if (!notSubscribedUuidsRef.current.includes(uuid)) {
                      newUuids = [...notSubscribedUuidsRef.current, uuid];
                      setNotSubscribedUuids(newUuids);
                      notSubscribedUuidsRef.current = newUuids;
                    }
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>
      <div
        css={css`
          width: 100%;
          max-width: 300px;
          margin: 0 auto;
          padding-top: 20px;
        `}
      >
        {showNotConnectedAlert && <ConnectionAlert />}
        <ButtonWithHoverAndPress
          isDisabled={false}
          shape="pill"
          type="unelevated"
          colorTheme="black"
          text={t(
            'CallJoin.CurrentlyOnCall.LeaveRecordingButtonLabel',
            'Leave recording',
            'Call with friends feature leave recording button text'
          )}
          size="md"
          isFullWidth={true}
          onPress={() => {
            setIsShowingLeaveConfirmationModal(true);
          }}
        />
      </div>

      <Modal
        aria-labelledby="do-you-want-to-leave-title"
        isShowingCloseButton
        onClose={() => {
          setIsShowingLeaveConfirmationModal(false);
        }}
        isShowing={isShowingLeaveConfirmationModal}
      >
        <Modal.Title id="do-you-want-to-leave-title">
          {t(
            'CallJoin.CurrentlyOnCall.LeaveRecordingConfirmationModal.Header',
            'Are you sure you want to leave this recording?',
            'Call with friends feature leave recording confirmation modal header'
          )}
        </Modal.Title>
        <Modal.Footer>
          <ButtonPrimary
            colorSet="negative"
            // WHEEL-984: remove this when encore colors are updated in  v5.18.4
            css={css`
              color: white !important;
            `}
            onClick={() => {
              onPressLeave();
            }}
          >
            {t(
              'CallJoin.CurrentlyOnCall.LeaveRecordingConfirmationModal.LeaveButtonLabel',
              'Leave',
              'Call with friends feature leave recording confirmation button text'
            )}
          </ButtonPrimary>
          <ButtonPrimary
            onClick={() => {
              setIsShowingLeaveConfirmationModal(false);
            }}
          >
            {t(
              'CallJoin.CurrentlyOnCall.LeaveRecordingConfirmationModal.StayButtonLabel',
              'Stay connected',
              'Call with friends feature stay in recording button text'
            )}
          </ButtonPrimary>
        </Modal.Footer>
      </Modal>
      <Modal aria-labelledby="trying-to-connect-modal" isShowing={!isOnline}>
        <Modal.Title id="trying-to-connect-modal">
          {t(
            'CallJoin.CurrentlyOnCall.ReconnectingToCallModal.Header',
            'Trying to reconnect...',
            'Call with friends feature, reconnecting to call modal header'
          )}
        </Modal.Title>
        <Modal.Body
          as="div"
          css={css`
            margin-top: 32px;
          `}
        >
          <LoadingContainer>
            <CenteredLoadingIndicator size="md" />
          </LoadingContainer>
          {t(
            'CallJoin.CurrentlyOnCall.ReconnectingToCallModal.Message',
            "If we're unable to reconnect within a few minutes, we'll end the call so you can try again.",
            'Call with friends feature, reconnecting to call modal message'
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export { CurrentlyOnCall };
